import { useEffect, useRef, useState } from "react";
import { ANSWER_LISTEN_MS, type LobbySnapshot } from "@themix/shared";

export type StoredPlayer = { playerId: string; reconnectToken?: string; token?: string; code?: string };

export function readStoredPlayer(): StoredPlayer | null {
  try {
    const raw = sessionStorage.getItem("themix_player") || localStorage.getItem("themix_player");
    return raw ? (JSON.parse(raw) as StoredPlayer) : null;
  } catch {
    return null;
  }
}

export function storedPlayerFor(code: string): StoredPlayer | null {
  const stored = readStoredPlayer();
  if (!stored?.playerId) return null;
  if (stored.code && stored.code.toUpperCase() !== code.toUpperCase()) return null;
  return stored;
}

export function hasCredentialsForRoom(code: string): boolean {
  const stored = readStoredPlayer();
  return Boolean(stored?.playerId && stored.code && stored.code.toUpperCase() === code.toUpperCase());
}

export function hasSeatForRoom(code: string): boolean {
  const stored = readStoredPlayer();
  if (!stored?.playerId) return false;
  if (!(stored.reconnectToken || stored.token)) return false;
  if (stored.code && stored.code.toUpperCase() !== code.toUpperCase()) return false;
  return true;
}

export function saveStoredPlayer(data: StoredPlayer): void {
  const blob = JSON.stringify(data);
  sessionStorage.setItem("themix_player", blob);
  localStorage.setItem("themix_player", blob);
}

export function clearStoredPlayer(): void {
  try {
    sessionStorage.removeItem("themix_player");
    localStorage.removeItem("themix_player");
  } catch {
    /* ignore */
  }
}

export function useTouchLike(): boolean {
  const [touch, setTouch] = useState(() => isTouchLike());
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setTouch(isTouchLike());
    update();
    mq.addEventListener("change", update);
    window.addEventListener("resize", update);
    return () => {
      mq.removeEventListener("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return touch;
}

function isTouchLike(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(pointer: coarse)").matches) return true;
  if (navigator.maxTouchPoints > 1) return true;
  return false;
}

export function roundDeadlineMs(snap: LobbySnapshot, now = Date.now()): number | null {
  const round = snap.round;
  if (!round) return null;
  if (snap.phase === "lobby" || snap.phase === "finished" || snap.phase === "reveal") return null;
  if (snap.phase === "paused") return null;
  if (snap.phase === "countdown" && snap.countdownEndsAt) {
    const end = Date.parse(snap.countdownEndsAt);
    return Number.isFinite(end) ? end : now + 1000;
  }
  return now + Math.max(0, round.remainingMs || round.durationMs);
}

export function liveRemainSeconds(snap: LobbySnapshot, now = Date.now()): number {
  const round = snap.round;
  if (!round) return 0;
  if (snap.phase === "paused") return Math.max(0, Math.ceil(round.remainingMs / 1000));
  const deadline = roundDeadlineMs(snap, now);
  if (deadline == null) return 0;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

export function useLiveRemain(snap: LobbySnapshot | null): number {
  const [remain, setRemain] = useState(0);
  const deadlineRef = useRef(0);
  const pausedRemainRef = useRef<number | null>(null);
  const phase = snap?.phase;
  const roundId = snap?.round?.roundId ?? 0;
  const startedAt = snap?.round?.startedAt ?? "";
  const countdownEndsAt = snap?.countdownEndsAt ?? "";
  const remainingMs = snap?.round?.remainingMs ?? 0;
  const durationMs = snap?.round?.durationMs ?? 0;

  useEffect(() => {
    if (!phase || phase === "lobby" || phase === "finished" || phase === "reveal") {
      deadlineRef.current = 0;
      pausedRemainRef.current = null;
      setRemain(0);
      return;
    }
    if (phase === "paused") {
      deadlineRef.current = 0;
      pausedRemainRef.current = Math.max(0, Math.ceil(remainingMs / 1000));
      setRemain(pausedRemainRef.current);
      return;
    }
    pausedRemainRef.current = null;
    const now = Date.now();
    const deadline =
      phase === "countdown" && countdownEndsAt
        ? Date.parse(countdownEndsAt) || now + 1000
        : now + Math.max(0, remainingMs || durationMs);
    deadlineRef.current = deadline;
    setRemain(Math.max(0, Math.ceil((deadline - now) / 1000)));
  }, [phase, roundId, startedAt, countdownEndsAt]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (pausedRemainRef.current != null) {
        setRemain(pausedRemainRef.current);
        return;
      }
      const deadline = deadlineRef.current;
      if (!deadline) {
        setRemain((n) => (n === 0 ? n : 0));
        return;
      }
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemain((n) => (n === next ? n : next));
    }, 100);
    return () => window.clearInterval(id);
  }, []);

  return remain;
}

export function mergeLobbySnapshot(prev: LobbySnapshot | null, incoming: LobbySnapshot): LobbySnapshot {
  if (incoming.you) return incoming;
  if (prev?.you && prev.code === incoming.code) {
    return { ...incoming, you: prev.you, spectator: false };
  }
  return incoming;
}

export function useListenLock(roundKey: string | number | null | undefined, startedAt: string | null | undefined, active: boolean): {
  listenLock: boolean;
  listenLeft: number;
} {
  const [leftMs, setLeftMs] = useState(0);
  const unlockAtRef = useRef(0);
  const remainingRef = useRef(0);
  const lastKey = useRef<string | number | null>(null);

  useEffect(() => {
    if (roundKey == null || roundKey === "") {
      lastKey.current = null;
      unlockAtRef.current = 0;
      remainingRef.current = 0;
      setLeftMs(0);
      return;
    }

    if (lastKey.current !== roundKey) {
      if (!active) {
        setLeftMs(0);
        return;
      }
      lastKey.current = roundKey;
      const start = startedAt ? Date.parse(startedAt) : Date.now();
      unlockAtRef.current = (Number.isFinite(start) ? start : Date.now()) + ANSWER_LISTEN_MS;
      remainingRef.current = ANSWER_LISTEN_MS;
    } else if (!active) {
      remainingRef.current = Math.max(
        0,
        unlockAtRef.current ? unlockAtRef.current - Date.now() : remainingRef.current,
      );
      unlockAtRef.current = 0;
      setLeftMs(remainingRef.current);
      return;
    } else if (unlockAtRef.current === 0 && remainingRef.current > 0) {
      unlockAtRef.current = Date.now() + remainingRef.current;
    }

    const tick = () => {
      const left = Math.max(0, unlockAtRef.current - Date.now());
      remainingRef.current = left;
      setLeftMs(left);
    };
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [active, roundKey, startedAt]);

  return {
    listenLock: active && leftMs > 0,
    listenLeft: Math.ceil(leftMs / 1000),
  };
}

export function useRetryLock(untilMs: number, active: boolean): { retryLock: boolean; retryLeft: number } {
  const [leftMs, setLeftMs] = useState(0);

  useEffect(() => {
    if (!active || untilMs <= 0) {
      setLeftMs(0);
      return;
    }
    const tick = () => setLeftMs(Math.max(0, untilMs - Date.now()));
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [active, untilMs]);

  return {
    retryLock: active && leftMs > 0,
    retryLeft: Math.ceil(leftMs / 1000),
  };
}

export const SYSTEM_REJECT_REASONS = new Set([
  "listen",
  "rate",
  "paused",
  "not_playing",
  "unknown",
  "spectator",
  "locked",
  "cooldown",
]);
